import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { EXPORT_FESTIVAL, GET_EDITION, GET_EDITION_BANDS } from "./graphql";
import { GET_ANNOUNCEMENTS } from "../announcement/graphql";
import { DELETE_BOOKING } from "../booking/graphql";
import {
  Button,
  FormControlLabel,
  Switch,
  Stack,
  Typography,
  Card,
  CardContent,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState, SyntheticEvent } from "react";
import type { ReactNode } from "react";
import { BandBooking } from "../../__generated__/graphql";
import dayjs from "dayjs";
import BandsTables from "./tables";
import { toast } from "react-toastify";
import { LoadingOverlay } from "../../components/Loading";
import { useIsSuperAdmin } from "../../App";
import { LinkButton } from "../../components/LinkButton";
import {
  ScheduleDaySelector,
  DayOption,
} from "../../components/ScheduleDaySelector";
import { TabbedPage } from "../../components/TabbedPage";
import { EditEditionBandsContent } from "../editEditionsBands";
import { CREATE_POST, UPDATE_POST_LINKS } from "../post/graphql";
import { newsPostDefaults } from "../post/SocialLinks";
import { confirmAlert } from "react-confirm-alert";
import BookingBuilder, { BookingBuilderAnnouncement } from "./BookingBuilder";
import { parseBooleanEnvFlag } from "../../utils/envFlags";

export type EditionBand = BandBooking & {
  source: "Edition" | string;
};

const ALL_DAYS_KEY = "__ALL_DAYS__";
const DEFAULT_POST_DESCRIPTION = "Placeholder message";

type PostLinkPayload = {
  type: "artist";
  linkedItemName: string;
  linkedId: string;
  description: string;
};

const EditionBands = () => {
  const { id } = useParams();
  const [announcementFilters, setAnnouncementFilters] = useState<
    { [s: string]: boolean } | undefined
  >(undefined);
  const { data, loading, refetch: refetchEditionBands } = useQuery(
    GET_EDITION_BANDS as any,
    {
      variables: {
        id: id!,
      },
      fetchPolicy: "cache-and-network",
    }
  );
  const { data: announcementsQueryData, refetch: refetchAnnouncements } = useQuery(
    GET_ANNOUNCEMENTS,
    {
      skip: !id,
      variables: {
        editionId: id!,
      },
      fetchPolicy: "cache-and-network",
    }
  );
  const [bands, setBands] = useState<Map<string, EditionBand[]>>(
    new Map<string, EditionBand[]>()
  );
  const [selectedDate, setSelectedDate] = useState<string | undefined>(
    ALL_DAYS_KEY
  );
  const [groupByAnnouncement, setGroupByAnnouncement] = useState(false);
  const [showImageSizes, setShowImageSizes] = useState(false);
  const [activeAnnouncementId, setActiveAnnouncementId] = useState<
    string | null
  >(null);
  const [removingBookingId, setRemovingBookingId] = useState<string | null>(
    null
  );
  const [createPostMutation, { loading: creatingPost }] =
    useMutation(CREATE_POST);
  const [updatePostLinksMutation, { loading: updatingPostLinks }] =
    useMutation(UPDATE_POST_LINKS);
  const [deleteBooking] = useMutation(DELETE_BOOKING, {
    refetchQueries: id
      ? [
          { query: GET_EDITION_BANDS as any, variables: { id } },
          { query: GET_ANNOUNCEMENTS, variables: { editionId: id } },
        ]
      : [],
    awaitRefetchQueries: true,
    onCompleted: () => {
      setRemovingBookingId(null);
      toast("Booking removed from announcement");
    },
    onError: (error) => {
      setRemovingBookingId(null);
      const message = error instanceof Error ? error.message : "Unknown error";
      toast.error(`Failed to remove booking: ${message}`);
    },
  });
  const navigate = useNavigate();
  const TAB_SCHEDULE = 0;
  const TAB_ANNOUNCEMENTS = 1;
  const TAB_BULK_EDIT = 2;

  const [tabIndex, setTabIndex] = useState(TAB_SCHEDULE);
  const isSuperAdmin = useIsSuperAdmin();
  const canUseBookingBuilder = parseBooleanEnvFlag(
    process.env.REACT_APP_ENABLE_BOOKING_BUILDER,
    isSuperAdmin
  );
  const announcements = useMemo(
    () =>
      data?.festivalEdition?.announcements?.flatMap((item: any) => {
        if (announcementFilters?.[item.name] === false) return [];

        return item?.bands?.map((band: any) => {
          return {
            ...band,
            source: `${item.name}`,
          } as EditionBand;
        });
      }) ?? [],
    [announcementFilters, data]
  );

  const editionBands: EditionBand[] =
    data?.festivalEdition?.bands?.map((item: any) => {
      return {
        ...item,
        source: "Edition",
      } as EditionBand;
    }) ?? [];
  // const announcementBands =
  //   announcements?.map((item) => {
  //     return {
  //       ...item,
  //       source: `Announcement : ${item.}` ,
  //     } as EditionBand;
  //   }) ?? [];

  useEffect(() => {
    if (!editionBands && !announcements) return;

    const bandsPerDay = [...editionBands, ...announcements].reduce<
      Map<string, EditionBand[]>
    >((prev: Map<string, EditionBand[]>, next: EditionBand) => {
      const date = dayjs(next.startTime).utc(true);

      let effectDate = date.format("YYYY/MM/DD");

      if (date.hour() < 5) {
        effectDate = date.subtract(1, "day").format("YYYY/MM/DD");
      }

      const key = date.isValid() ? effectDate : "No Date";

      const bands = prev.get(key) ?? [];

      prev.set(key, [...bands, next]);

      return prev;
    }, new Map());
    setBands(bandsPerDay);
  }, [data, announcements, announcementsQueryData]);

  useEffect(() => {
    const sourceAnnouncements =
      announcementsQueryData?.announcements ??
      data?.festivalEdition?.announcements ??
      [];

    if (sourceAnnouncements.length === 0) {
      setAnnouncementFilters(undefined);
      return;
    }

    setAnnouncementFilters((prev) => {
      const next = sourceAnnouncements.reduce(
        (acc: Record<string, boolean>, item: any) => {
          if (!item?.name) return acc;
          acc[item.name] = prev?.[item.name] ?? true;
          return acc;
        },
        {}
      );

      if (!prev) return next;

      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      if (prevKeys.length !== nextKeys.length) return next;
      if (nextKeys.some((key) => prev[key] !== next[key])) return next;

      return prev;
    });
  }, [announcementsQueryData, data]);

  const [exportFestival] = useLazyQuery(EXPORT_FESTIVAL, {
    onCompleted: (data) => {
      console.log("data", data);
      toast("App updated");
      setTimeout(
        () => navigate(`/notifications/${data.clashfinderExport}`),
        2000
      );
    },
    onError: () => {
      toast("Error updating lineup");
    },
  });

  const handleSelectDay = (selectedDate: string) => {
    setSelectedDate(selectedDate);
  };

  const handleRemoveBookingFromAnnouncement = (
    bookingId: string,
    bandName: string
  ) => {
    if (!bookingId) {
      toast.error("Unable to remove booking: missing booking id.");
      return;
    }

    confirmAlert({
      title: "Remove booking from announcement?",
      message: `Remove ${bandName} from this announcement?`,
      buttons: [
        {
          label: "Remove booking",
          onClick: () => {
            setRemovingBookingId(bookingId);
            void deleteBooking({ variables: { bookingId } });
          },
        },
        {
          label: "Cancel",
          onClick: () => {},
        },
      ],
    });
  };

  const keys = useMemo(() => Array.from(bands.keys()), [bands]);
  const sortedKeys = useMemo(
    () =>
      [...keys].sort((a, b) => {
        const aDate = dayjs(a);
        const bDate = dayjs(b);
        const aValue = aDate.isValid()
          ? aDate.valueOf()
          : Number.MAX_SAFE_INTEGER;
        const bValue = bDate.isValid()
          ? bDate.valueOf()
          : Number.MAX_SAFE_INTEGER;
        return aValue - bValue;
      }),
    [keys]
  );

  useEffect(() => {
    if (sortedKeys.length === 0) {
      return;
    }

    if (!selectedDate) {
      setSelectedDate(ALL_DAYS_KEY);
      return;
    }

    if (selectedDate !== ALL_DAYS_KEY && !sortedKeys.includes(selectedDate)) {
      setSelectedDate(ALL_DAYS_KEY);
    }
  }, [sortedKeys, selectedDate]);

  const defaultDate = sortedKeys[0];
  const selectedBands = useMemo(() => {
    if (selectedDate === ALL_DAYS_KEY) {
      return Array.from(bands.values()).flat();
    }

    const key = selectedDate ?? defaultDate;
    if (!key) return undefined;

    return bands.get(key);
  }, [bands, selectedDate, defaultDate]);
  const effectiveSelectedDate =
    selectedDate && selectedDate !== ALL_DAYS_KEY ? selectedDate : null;

  const dayOptions: DayOption[] = useMemo(() => {
    const options: DayOption[] = [];

    if (sortedKeys.length > 0) {
      options.push({
        key: ALL_DAYS_KEY,
        title: "All Days",
        subtitle: `${sortedKeys.length} day${
          sortedKeys.length === 1 ? "" : "s"
        }`,
      });
    }

    sortedKeys.forEach((item) => {
      const isValid = dayjs(item).isValid();
      options.push({
        key: item,
        title: isValid ? dayjs(item).format("dddd DD") : "No Date",
        subtitle: isValid ? dayjs(item).format("YYYY/MM/DD") : "-",
      });
    });

    return options;
  }, [sortedKeys]);

  const selectedDayKey = selectedDate ?? defaultDate ?? dayOptions[0]?.key;

  // const handleExport = () => {
  //   let name = data?.festivalEdition?.name!;

  //   exportFestival({
  //     variables: {
  //       name: name,
  //       id: id!,
  //     },
  //   });
  // };

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const announcementsList = useMemo(() => {
    const announcements = (announcementsQueryData?.announcements ??
      data?.festivalEdition?.announcements ??
      []) as any[];

    return [...announcements].sort((a, b) => {
      const aDate = dayjs(a?.availabilityDate);
      const bDate = dayjs(b?.availabilityDate);

      const aValue = aDate.isValid()
        ? aDate.valueOf()
        : Number.MAX_SAFE_INTEGER;
      const bValue = bDate.isValid()
        ? bDate.valueOf()
        : Number.MAX_SAFE_INTEGER;

      if (aValue === bValue) {
        return (a?.name ?? "").localeCompare(b?.name ?? "");
      }

      return aValue - bValue;
    });
  }, [announcementsQueryData, data]);
  const hasAnnouncements = announcementsList.length > 0;

  const bookingBuilderAnnouncements = useMemo<BookingBuilderAnnouncement[]>(
    () =>
      announcementsList
        .map((announcement: any) => {
          const announcementId =
            typeof announcement?.id === "string" ? announcement.id : null;
          const announcementName =
            typeof announcement?.name === "string" ? announcement.name : null;

          if (!announcementId || !announcementName) return null;

          const bands = Array.isArray(announcement?.bands)
            ? announcement.bands.map((booking: any) => ({
                band: {
                  id:
                    typeof booking?.band?.id === "string"
                      ? booking.band.id
                      : null,
                },
              }))
            : [];

          return {
            id: announcementId,
            name: announcementName,
            bands,
          };
        })
        .filter(
          (announcement): announcement is BookingBuilderAnnouncement =>
            Boolean(announcement)
        ),
    [announcementsList]
  );

  const handleBookingsCreated = async () => {
    await Promise.allSettled([refetchEditionBands(), refetchAnnouncements()]);
  };

  const handleToggleAnnouncementFilter = (name: string, checked: boolean) => {
    setAnnouncementFilters((prev) => ({
      ...(prev ?? {}),
      [name]: checked,
    }));
  };

  const isCreatingAnnouncementPost = (announcementId: string) =>
    activeAnnouncementId === announcementId &&
    (creatingPost || updatingPostLinks);

  const handleCreateAnnouncementPost = async (announcement: any) => {
    if (!announcement?.name || !announcement?.id) return;

    const linksPayload = (announcement?.bands ?? []).reduce(
      (acc: Record<string, PostLinkPayload>, band: any) => {
        const bandId = band?.band?.id;
        const bandName = band?.band?.name ?? "";
        if (!bandId) return acc;

        if (!acc[bandId]) {
          acc[bandId] = {
            type: "artist",
            linkedItemName: bandName,
            linkedId: bandId,
            description: bandName,
          };
        }

        return acc;
      },
      {}
    );

    if (Object.keys(linksPayload).length === 0) {
      toast("No bands available to link for this announcement.");
      return;
    }

    setActiveAnnouncementId(announcement.id);
    const postDate = (() => {
      const availabilityDate = announcement.availabilityDate;
      if (!availabilityDate) return new Date().toISOString();

      const parsedDate = new Date(availabilityDate);
      return Number.isNaN(parsedDate.valueOf())
        ? new Date().toISOString()
        : parsedDate.toISOString();
    })();
    try {
      const { data } = await createPostMutation({
        variables: {
          title: announcement.name,
          description: DEFAULT_POST_DESCRIPTION,
          date: postDate,
          image: "",
          socials: newsPostDefaults,
        },
      });

      const postId = data?.createPost.id;
      if (!postId) {
        throw new Error("No post id returned from createPost mutation.");
      }

      await updatePostLinksMutation({
        variables: {
          id: postId,
          links: linksPayload,
        },
      });

      const bandsAdded = Object.keys(linksPayload).length;

      toast(
        `Post created from announcement, ${bandsAdded} artists were linked`
      );
      navigate(`/post/${postId}`);
    } catch (error) {
      console.error(error);
      toast("Unable to create post for this announcement.");
    } finally {
      setActiveAnnouncementId(null);
    }
  };

  const renderAnnouncementFilters = () => {
    if (!hasAnnouncements) return null;

    return (
      <Stack spacing={1}>
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
        >
          Announcement filters
        </Typography>
        <Stack
          direction="row"
          spacing={1.5}
          useFlexGap
          flexWrap="wrap"
          alignItems="center"
        >
          {announcementsList.map((item: any) => {
            const value = announcementFilters?.[item.name] ?? true;
            return (
              <FormControlLabel
                key={item.name}
                control={
                  <Switch
                    checked={value}
                    onChange={(_, checked) =>
                      handleToggleAnnouncementFilter(item.name, checked)
                    }
                  />
                }
                label={item.name}
              />
            );
          })}
        </Stack>
      </Stack>
    );
  };

  if (loading) return <LoadingOverlay loading={loading} />;

  if (!data?.festivalEdition) return <div>edition not found</div>;

  const headerActions = (
    <Stack
      direction="row"
      spacing={1.5}
      useFlexGap
      flexWrap="wrap"
      alignItems="center"
    >
      <Button
        variant="contained"
        color="secondary"
        onClick={() => {
          navigate(`/announcement/edit/${id}`);
        }}
      >
        Create Announcement
      </Button>
      {/* <Button variant="contained" color="secondary" onClick={handleExport}>
        Update App
      </Button> */}
      <Button
        variant="outlined"
        onClick={() => {
          navigate(`/edition/edit/${id}`);
        }}
      >
        Edit settings
      </Button>
      <Button
        variant="outlined"
        onClick={() => {
          window.open(`/clashfinder/${id}`, "_blank");
        }}
      >
        View Clashfinder
      </Button>
    </Stack>
  );

  const renderGroupingControls = (includeGroupBy = true) => {
    const controls: ReactNode[] = [];

    if (includeGroupBy) {
      controls.push(
        <FormControlLabel
          key="group-by-announcement"
          control={
            <Switch
              checked={groupByAnnouncement}
              onChange={(_, checked) => setGroupByAnnouncement(checked)}
            />
          }
          label="Group by announcement"
        />
      );
    }

    if (isSuperAdmin) {
      controls.push(
        <FormControlLabel
          key="show-image-sizes"
          control={
            <Switch
              checked={showImageSizes}
              onChange={(_, checked) => setShowImageSizes(checked)}
            />
          }
          label="Show image sizes"
        />
      );
    }

    if (controls.length === 0) return null;

    return (
      <Stack
        direction="row"
        spacing={1.5}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
      >
        {controls}
      </Stack>
    );
  };

  const scheduleContent = (
    <Stack spacing={3}>
      <Card elevation={1}>
        <CardContent>
          <Stack spacing={3}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}
            >
              Select a day
            </Typography>
            {dayOptions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No bookings found for this edition.
              </Typography>
            ) : (
              <ScheduleDaySelector
                days={dayOptions}
                selectedKey={selectedDayKey}
                onSelect={handleSelectDay}
              />
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card elevation={1}>
        <CardContent>
          <Stack spacing={3}>
            {renderAnnouncementFilters()}
            {renderGroupingControls()}
            {selectedBands ? (
              <BandsTables
                bands={selectedBands}
                eventTimezone={data?.festivalEdition?.timezone!}
                groupBy={groupByAnnouncement ? "announcement" : "stage"}
                showImageSizes={isSuperAdmin && showImageSizes}
                onRemoveBooking={handleRemoveBookingFromAnnouncement}
                removingBookingId={removingBookingId}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Select a day to view bookings.
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );

  const announcementsContent = (
    <Stack spacing={3}>
      <Card elevation={1}>
        <CardContent>
          {hasAnnouncements ? (
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="announcements table">
                <TableHead>
                  <TableRow>
                    <TableCell align="left">name</TableCell>
                    <TableCell align="left">announcement date</TableCell>
                    <TableCell align="left">actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {announcementsList.map((announcement: any) => (
                    <TableRow key={announcement.id}>
                      <TableCell align="left">{announcement.name}</TableCell>
                      <TableCell align="left">
                        {announcement.availabilityDate
                          ? dayjs(announcement.availabilityDate).format(
                              "YYYY/MM/DD HH:mm"
                            )
                          : "-"}
                      </TableCell>
                      <TableCell align="left">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <LinkButton
                            path={`/announcement/${announcement.id}`}
                            text="Edit Bands"
                          />
                          <LinkButton
                            path={`/announcement/edit/${announcement.editionId}/${announcement.id}`}
                            text="Edit"
                          />
                          <Button
                            variant="text"
                            size="small"
                            onClick={() =>
                              handleCreateAnnouncementPost(announcement)
                            }
                            disabled={isCreatingAnnouncementPost(
                              announcement.id
                            )}
                          >
                            {isCreatingAnnouncementPost(announcement.id)
                              ? "Creating..."
                              : "Create news post"}
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No announcements created yet.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Stack>
  );

  const tabs = [
    {
      label: "Schedule",
      id: "edition-tab-0",
      ariaControlsId: "edition-tabpanel-0",
      content: scheduleContent,
    },
    {
      label: "Announcements",
      id: "edition-tab-1",
      ariaControlsId: "edition-tabpanel-1",
      content: announcementsContent,
      disabled: !hasAnnouncements,
    },
    {
      label: "Edit Bands",
      id: "edition-tab-2",
      ariaControlsId: "edition-tabpanel-2",
      content: <EditEditionBandsContent key="bulk-edit-tab" inline />,
    },
    ...(canUseBookingBuilder
      ? [
          {
            label: "Booking Builder",
            id: "edition-tab-3",
            ariaControlsId: "edition-tabpanel-3",
            content: (
              <BookingBuilder
                announcements={bookingBuilderAnnouncements}
                onBookingsCreated={handleBookingsCreated}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <LoadingOverlay loading={loading}>
      <TabbedPage
        title={data?.festivalEdition?.name ?? ""}
        actions={headerActions}
        value={tabIndex}
        onChange={handleTabChange}
        tabs={tabs}
      />
    </LoadingOverlay>
  );
};

export default EditionBands;
